import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Paper
} from '@mui/material';
import {
  Download,
  PersonAdd,
  PlayArrow,
  CheckCircle,
  Window,
  Apple
} from '@mui/icons-material';
import '../styles/Join-us.css';

const DISCORD_URL = 'https://discord.gg/dTuaMqvSAr';

const JoinUs = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('user'));

  useEffect(() => {
    const sync = () => setIsLoggedIn(!!localStorage.getItem('user'));
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const steps = [
    {
      label: 'Créez votre compte',
      description: 'Inscrivez-vous sur notre site Web pour pouvoir vous connecter au jeu.',
      icon: <PersonAdd fontSize="large" />,
      color: '#B67A3C'
    },
    {
      label: 'Téléchargez le launcher',
      description: 'Récupérez la version Windows ou Mac directement ci-dessous.',
      icon: <Download fontSize="large" />,
      color: '#C9CED6'
    },
    {
      label: 'Lancez votre aventure',
      description: 'Ouvrez le launcher, connectez-vous avec vos identifiants, et rejoignez Nexus !',
      icon: <PlayArrow fontSize="large" />,
      color: '#F5F3FF'
    }
  ];

  const features = [
    { title: 'Pas de Pay-to-Win', description: 'Équité totale pour tous les joueurs' },
    { title: 'Rates Optimisés', description: 'Progression équilibrée et fun' },
    { title: 'Support Actif', description: 'Équipe disponible 7j/7' },
    { title: 'Anti-Bot', description: 'Environnement sécurisé' },
    { title: 'Événements Réguliers', description: 'Nouveautés chaque semaine' },
    { title: 'Communauté FR', description: 'Joueurs francophones accueillants' }
  ];

  return (
    <Box className="join-us-page">
      <Container maxWidth="lg" sx={{ paddingTop: '100px', paddingBottom: '80px' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', marginBottom: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #B67A3C 0%, #F5F3FF 55%, #C9CED6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Rejoignez Nexus
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '800px',
              margin: '0 auto',
              fontSize: { xs: '1rem', md: '1.3rem' }
            }}
          >
            Créez votre compte, téléchargez le launcher officiel, et plongez au cœur des failles temporelles.
          </Typography>
        </Box>

        {/* Steps */}
        <Box sx={{ marginBottom: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#fff',
              textAlign: 'center',
              marginBottom: 4
            }}
          >
            Comment nous rejoindre ?
          </Typography>
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'linear-gradient(145deg, rgba(31, 37, 68, 0.9) 0%, rgba(26, 26, 46, 0.9) 100%)',
                    border: `2px solid ${step.color}40`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      borderColor: step.color,
                      boxShadow: `0 12px 30px ${step.color}40`
                    }
                  }}
                >
                  <CardContent sx={{ padding: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `${step.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        color: step.color
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: '#fff', marginBottom: 2 }}
                    >
                      Étape {index + 1}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: step.color, marginBottom: 2 }}
                    >
                      {step.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Grid */}
        <Box sx={{ marginBottom: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#fff',
              textAlign: 'center',
              marginBottom: 4
            }}
          >
            Pourquoi Nexus ?
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  sx={{
                    padding: 3,
                    background: 'rgba(31, 37, 68, 0.6)',
                    border: '1px solid rgba(201, 206, 214, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#C9CED6',
                      background: 'rgba(31, 37, 68, 0.8)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle sx={{ color: '#B67A3C', fontSize: 30 }} />
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: '#C9CED6', marginBottom: 0.5 }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Buttons */}
        <Box
          sx={{
            textAlign: 'center',
            padding: 6,
            background: 'linear-gradient(135deg, rgba(31, 37, 68, 0.8) 0%, rgba(26, 26, 46, 0.8) 100%)',
            borderRadius: 4,
            border: '2px solid rgba(201, 206, 214, 0.3)'
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: '#C9CED6', marginBottom: 3 }}
          >
            Prêt à démarrer ?
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255, 255, 255, 0.78)', marginBottom: 4 }}
          >
            Téléchargez le launcher officiel pour commencer à jouer.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            {/* Bouton Windows */}
            <Button
              component="a"
              href="https://updater.nexus-temporel.com/setup/download/windows/exe"
              variant="contained"
              size="large"
              startIcon={<Window />}
              sx={{
                background: 'linear-gradient(135deg, #1976D2 0%, #115293 100%)',
                color: '#fff',
                padding: '12px 30px',
                fontSize: '1.1rem',
                fontWeight: 700,
                '&:hover': {
                  background: 'linear-gradient(135deg, #115293 0%, #0D47A1 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(25, 118, 210, 0.4)'
                }
              }}
            >
              Windows
            </Button>

            {/* Bouton Mac */}
            <Button
              component="a"
              href="https://updater.nexus-temporel.com/setup/download/macos/pkg"
              variant="contained"
              size="large"
              startIcon={<Apple />}
              sx={{
                background: 'linear-gradient(135deg, #1976D2 0%, #115293 100%)',
                color: '#fff',
                padding: '12px 30px',
                fontSize: '1.1rem',
                fontWeight: 700,
                '&:hover': {
                  background: 'linear-gradient(135deg, #115293 0%, #0D47A1 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(25, 118, 210, 0.4)'
                }
              }}
            >
              Mac
            </Button>
          </Box>

          <Box sx={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.1)', marginBottom: 4 }} />

          {/* Boutons Compte & Discord */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {!isLoggedIn && (
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                size="large"
                startIcon={<PersonAdd />}
                sx={{
                  background: 'linear-gradient(135deg, #C9CED6 0%, #B67A3C 100%)',
                  color: '#1a1a2e',
                  padding: '12px 40px',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B67A3C 0%, #8D5A2A 100%)',
                    color: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(182, 122, 60, 0.4)'
                  }
                }}
              >
                Créer un Compte
              </Button>
            )}
            <Button
              component="a"
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#5865F2',
                color: '#5865F2',
                padding: '12px 40px',
                fontSize: '1.1rem',
                fontWeight: 700,
                borderWidth: '2px',
                '&:hover': {
                  background: 'rgba(88, 101, 242, 0.1)',
                  borderColor: '#4752C4',
                  borderWidth: '2px',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Rejoindre le Discord
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default JoinUs;